import { useEffect, useState } from 'react';
import { blobStoreService } from '@/core/api/services';
import { SAMPLE_DOCUMENT_BLOB_ID } from '../types';
// The bundled synthetic record. It is reachable ONLY through the sample
// sentinel below, which only the mock source ever sets — see the module note.
import sampleRecordUrl from '../assets/sample-anesthesia-record.pdf?url';

/**
 * Resolves a case's source anesthesia record for the document viewer.
 *
 * # The failure this exists to end
 *
 * Both document panes used to render the bundled synthetic sample
 * unconditionally, ignoring the case entirely. A reviewer adjudicating real
 * coded results was therefore comparing them against a document belonging to
 * no patient — an audit tool returning verdicts that look considered and are
 * meaningless. The "Sample" badge did not save it: a labelled irrelevant
 * document is still not the record, and the label sat in chrome the reviewer
 * stops seeing by the tenth case.
 *
 * So the sample is now gated behind a sentinel that only the MOCK source sets
 * (SAMPLE_DOCUMENT_BLOB_ID). A live case with no stored PDF has an empty
 * documentBlobId and resolves to `none`, which the pane renders as an explicit
 * "no document" state. There is deliberately no fallback path from `none` to
 * the sample — that fallback is the bug.
 *
 * # Copy at ingest, not proxy on demand
 *
 * The id handed in here is a blobstore id, not an FA S3 location, because the
 * document is COPIED into our blobstore when the case is ingested (the SFTP
 * listener already does this: it stores the upload and puts the id on
 * event.BlobRefs, which evalmaterializer folds into readmodel.Case.BlobRef and
 * evalreview publishes as document_blob_id). Reads here never touch FA's
 * bucket. That choice over proxying to FA on demand, for three reasons:
 *
 *  - The bucket lives in FA's AWS account. Proxying would put a live,
 *    per-reviewer-click dependency on a third party's credentials and uptime
 *    inside the review loop; a rotated key or a network path change would take
 *    the workbench down mid-study, and the reviewer would read it as "this
 *    case has no document".
 *  - An eval study re-reads the same record many times — every reviewer on a
 *    case, every revisit, every pop-out. Copying once at ingest turns N reads
 *    of PHI across an external boundary into one, which is both the cheaper
 *    and the smaller-attack-surface shape.
 *  - The eval needs a document that is STABLE for the length of the study. A
 *    proxy returns whatever FA holds right now; if a scan is re-uploaded or
 *    purged mid-study, reviewer verdicts stop being reproducible against the
 *    document that produced them.
 *
 * The cost we accept is a second copy of PHI at rest in our blobstore, and an
 * ingest-time fetch that can fail independently of the review path.
 *
 * # Why bytes are streamed, not linked
 *
 * The PDF is fetched over the authenticated ConnectRPC transport and turned
 * into an object URL, rather than pointed at by a plain href. A plain URL to a
 * PHI document is a credential-free handle that lands in browser history, gets
 * copied into tickets, and survives logout. The object URL is process-local
 * and revoked on unmount, so the bytes leave memory when the pane closes.
 */
export type CaseDocument =
  | { status: 'none' }
  | { status: 'loading' }
  | { status: 'ready'; url: string; isSample: boolean }
  | { status: 'error' };

export function useCaseDocument(documentBlobId: string): CaseDocument {
  const [doc, setDoc] = useState<CaseDocument>(() => initialFor(documentBlobId));

  useEffect(() => {
    // Empty id and the mock sentinel both resolve synchronously — no request,
    // and in particular no request for the id "" (which the server would
    // answer with a not-found the pane would have to disambiguate from a real
    // fetch failure).
    const immediate = initialFor(documentBlobId);
    if (immediate.status !== 'loading') {
      setDoc(immediate);
      return;
    }

    let cancelled = false;
    let objectUrl = '';
    setDoc({ status: 'loading' });

    (async () => {
      try {
        const chunks: Uint8Array[] = [];
        let contentType = '';
        // Timing, not tracing: the server already reports its own read duration
        // as a metric, so the number nobody had was the CLIENT leg — transfer
        // plus decode — which is where a "documents feel slow" report actually
        // lives. Deliberately no blob id and no document bytes: size and
        // durations are diagnostics, an id is an identifier.
        const t0 = performance.now();
        let tFirstChunk = 0;
        let bytes = 0;
        for await (const res of blobStoreService.download({ id: documentBlobId })) {
          if (res.data.case === 'metadata') {
            contentType = res.data.value.contentType;
          } else if (res.data.case === 'chunk') {
            if (tFirstChunk === 0) tFirstChunk = performance.now();
            bytes += res.data.value.byteLength;
            chunks.push(res.data.value);
          }
        }
        const tDone = performance.now();
        console.debug(
          `case document: ${(bytes / 1024).toFixed(0)}KB in ${chunks.length} chunks — ` +
            `first byte ${(tFirstChunk - t0).toFixed(0)}ms, ` +
            `transfer+decode ${(tDone - (tFirstChunk || t0)).toFixed(0)}ms, ` +
            `total ${(tDone - t0).toFixed(0)}ms`,
        );
        if (cancelled) return;
        if (chunks.length === 0) {
          // A zero-byte blob is a broken reference, not a document. Surface it
          // as an error so it reads as "something is wrong" rather than
          // handing PDF.js empty bytes and showing a blank page.
          setDoc({ status: 'error' });
          return;
        }
        objectUrl = URL.createObjectURL(
          new Blob(chunks as BlobPart[], { type: contentType || 'application/pdf' }),
        );
        setDoc({ status: 'ready', url: objectUrl, isSample: false });
      } catch {
        if (cancelled) return;
        // The error object is deliberately dropped rather than logged — a
        // blobstore failure can echo blob metadata, and this document is PHI.
        console.error('case document fetch failed');
        setDoc({ status: 'error' });
      }
    })();

    return () => {
      cancelled = true;
      // Drop the PHI bytes as soon as the pane goes away. Without this the
      // document stays resident for the life of the tab, across every
      // subsequent case the reviewer opens.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentBlobId]);

  return doc;
}

/** Resolution that needs no network: no id, or the mock-only sample sentinel. */
function initialFor(documentBlobId: string): CaseDocument {
  if (!documentBlobId) return { status: 'none' };
  if (documentBlobId === SAMPLE_DOCUMENT_BLOB_ID) {
    return { status: 'ready', url: sampleRecordUrl, isSample: true };
  }
  return { status: 'loading' };
}
